import React, {
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import type { ToolProps } from './Tool';
import { Tool } from './Tool';
import type { ValidIconName } from './Icon';
import { Icon } from './Icon';
import { DEFAULT_PLACEMENT_CONFIG, mergeRefs } from '../../util';
import type { PlacementType } from 'react-laag/dist/PlacementType';
import { useLayerDeferedVisibility } from '../../lib';
import { flushSync } from 'react-dom';

// ── Type Definitions ────────────────────────────────────────────────

/**
 * A non-interactive header label displayed above a group of menu items.
 */
export type MultiLevelMenuHeader = {
  id: string;
  type: 'header';
  label: string;
};

/**
 * A visual divider line between groups of menu items.
 */
export type MultiLevelMenuDivider = {
  type: 'divider';
};

/**
 * Common properties shared by all menu items.
 */
type MultiLevelMenuItemBase = {
  id: string;
  label: string;
  icon?: ValidIconName;
  disabled?: boolean;
};

/**
 * A leaf menu item that triggers an action when clicked.
 */
export type MultiLevelMenuLeafItem = MultiLevelMenuItemBase & {
  onClick: () => void;
};

/**
 * A branch menu item that opens a submenu with nested entries.
 */
export type MultiLevelMenuBranchItem = MultiLevelMenuItemBase & {
  children: MultiLevelMenuEntry[];
};

/**
 * A single menu item: either a leaf (has onClick) or a branch (has children).
 */
export type MultiLevelMenuItem =
  | MultiLevelMenuLeafItem
  | MultiLevelMenuBranchItem;

/**
 * A single entry in a multi-level menu: an item, a divider, or a header.
 */
export type MultiLevelMenuEntry =
  | MultiLevelMenuItem
  | MultiLevelMenuDivider
  | MultiLevelMenuHeader;

/**
 * Props for a {@link MultiLevelMenuTool}.
 *
 * Inherits all {@link ToolProps} except:
 * - `onClick` — handled internally (opens/closes the menu)
 * - `icon` — re-added as optional; defaults to `'more'`
 * - `tooltip` — re-added as required; no default to avoid hardcoded strings
 */
export type MultiLevelMenuToolProps = Omit<
  ToolProps,
  'onClick' | 'icon' | 'tooltip'
> &
  Partial<Pick<ToolProps, 'icon'>> &
  Required<Pick<ToolProps, 'tooltip'>> & {
    /** Menu entries to render. */
    items: MultiLevelMenuEntry[];
    /**
     * Returns the DOM element into which menu panels are portalled.
     * Defaults to `document.body` when omitted.
     * Pass the portlet root element in Liferay to keep panels inside
     * the correct stacking context and avoid z-index / overflow issues.
     */
    containerElement?: () => HTMLElement | null;
  };

// ── Type Guards ─────────────────────────────────────────────────────

function isDivider(entry: MultiLevelMenuEntry): entry is MultiLevelMenuDivider {
  return 'type' in entry && entry.type === 'divider';
}

function isHeader(entry: MultiLevelMenuEntry): entry is MultiLevelMenuHeader {
  return 'type' in entry && entry.type === 'header';
}

function isItem(entry: MultiLevelMenuEntry): entry is MultiLevelMenuItem {
  return !isDivider(entry) && !isHeader(entry);
}

function hasChildren(
  item: MultiLevelMenuItem
): item is MultiLevelMenuBranchItem {
  return 'children' in item && Array.isArray(item.children);
}

// ── Helpers ─────────────────────────────────────────────────────────

function getInteractiveItems(
  items: MultiLevelMenuEntry[]
): MultiLevelMenuItem[] {
  return items.filter((e): e is MultiLevelMenuItem => isItem(e));
}

const SUBMENU_PLACEMENT_CONFIG = {
  placement: 'right-start' as PlacementType,
  auto: true,
  snap: true,
  triggerOffset: 0,
  containerOffset: 16,
  overflowContainer: false,
  possiblePlacements: [
    'right-start',
    'right-end',
    'left-start',
    'left-end'
  ] as PlacementType[]
};

// Nested submenus may be portalled, so context keeps Tab close scoped to the root menu.
const RootMenuTabCloseContext = React.createContext<(() => void) | null>(null);

// ── SubMenuItem ─────────────────────────────────────────────────────

type SubMenuItemProps = {
  item: MultiLevelMenuItem;
  isActive: boolean;
  isFocused: boolean;
  onActivate: (id: string) => void;
  onDeactivate: () => void;
  onSelect: (item: MultiLevelMenuLeafItem) => void;
  onFocus: (id: string) => void;
  onHoverNonSubmenuItem: () => void;
  containerElement?: () => HTMLElement | null;
  menuId: string;
  keyboardModeRef: React.RefObject<boolean>;
};

function SubMenuItemComponent({
  item,
  isActive,
  isFocused,
  onActivate,
  onDeactivate,
  onSelect,
  onFocus,
  onHoverNonSubmenuItem,
  containerElement,
  menuId,
  keyboardModeRef
}: Readonly<SubMenuItemProps>): React.JSX.Element {
  const itemRef = useRef<HTMLButtonElement>(null);
  const hasSubMenu = hasChildren(item);
  const submenuId = useMemo(
    () => (hasSubMenu ? `${menuId}-sub-${item.id}` : undefined),
    [hasSubMenu, menuId, item.id]
  );

  // Only move DOM focus when navigating by keyboard; mouse hover must not steal focus.
  useLayoutEffect(() => {
    if (isFocused && keyboardModeRef.current && itemRef.current) {
      itemRef.current.focus();
    }
  }, [isFocused, keyboardModeRef]);

  // Close the submenu and return focus to this item (keyboard path only).
  const handleKeyboardClose = useCallback(() => {
    onDeactivate();
    itemRef.current?.focus();
  }, [onDeactivate]);

  // Switch off keyboard mode so the focus effect above stays silent during mouse interaction.
  const handleMouseEnter = useCallback(() => {
    keyboardModeRef.current = false;
    onFocus(item.id);
    if (hasSubMenu && !item.disabled) {
      onActivate(item.id);
    } else {
      onHoverNonSubmenuItem();
    }
  }, [
    hasSubMenu,
    item.id,
    item.disabled,
    onActivate,
    onFocus,
    onHoverNonSubmenuItem,
    keyboardModeRef
  ]);

  const handleClick = useCallback(() => {
    if (item.disabled) {
      return;
    }
    if (hasSubMenu) {
      onActivate(item.id);
    } else {
      onSelect(item as MultiLevelMenuLeafItem);
    }
  }, [item, hasSubMenu, onActivate, onSelect]);

  // ArrowRight opens a submenu; Enter/Space activates or opens depending on item type.
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (item.disabled) {
        return;
      }
      if (hasSubMenu && e.key === 'ArrowRight') {
        e.preventDefault();
        e.stopPropagation();
        keyboardModeRef.current = true;
        onActivate(item.id);
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        if (hasSubMenu) {
          keyboardModeRef.current = true;
          onActivate(item.id);
        } else {
          onSelect(item as MultiLevelMenuLeafItem);
        }
      }
    },
    [item, hasSubMenu, onActivate, onSelect, keyboardModeRef]
  );

  const classes = ['ehs-multilevel-menu-item'];
  if (hasSubMenu) {
    classes.push('ehs-multilevel-menu-item-has-children');
  }
  if (isActive) {
    classes.push('ehs-multilevel-menu-item-active');
  }
  if (item.disabled) {
    classes.push('ehs-multilevel-menu-item-disabled');
  }

  return (
    <>
      <button
        ref={itemRef}
        className={classes.join(' ')}
        role="menuitem"
        tabIndex={isFocused ? 0 : -1}
        aria-haspopup={hasSubMenu ? 'menu' : undefined}
        aria-expanded={hasSubMenu ? isActive : undefined}
        aria-controls={isActive ? submenuId : undefined}
        aria-disabled={item.disabled ? true : undefined}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onMouseEnter={handleMouseEnter}
        type="button"
        data-testid={item.id}
      >
        {item.icon && (
          <Icon
            className="ehs-multilevel-menu-item-icon"
            iconName={item.icon}
            aria-label={undefined}
          />
        )}
        <span className="ehs-multilevel-menu-item-label">{item.label}</span>
        {hasSubMenu && (
          <Icon
            className="ehs-multilevel-menu-item-arrow"
            iconName="arrow-line-right-s"
            aria-label={undefined}
          />
        )}
      </button>
      {hasSubMenu && isActive && (
        <NestedSubMenu
          items={hasSubMenu ? item.children : []}
          anchorRef={itemRef}
          onSelect={onSelect}
          onClose={onDeactivate}
          onKeyboardClose={handleKeyboardClose}
          containerElement={containerElement}
          menuId={submenuId!}
          menuLabel={item.label}
          keyboardModeRef={keyboardModeRef}
        />
      )}
    </>
  );
}

const SubMenuItem = React.memo(SubMenuItemComponent);

// ── MenuContent (shared rendering logic) ────────────────────────────

type MenuContentProps = {
  items: MultiLevelMenuEntry[];
  onSelect: (item: MultiLevelMenuLeafItem) => void;
  onClose: () => void;
  onKeyboardClose?: () => void;
  containerElement?: () => HTMLElement | null;
  menuId: string;
  menuLabel: string;
  isRoot?: boolean;
  layerProps?: Record<string, unknown>;
  keyboardModeRef: React.RefObject<boolean>;
};

function MenuContent({
  items,
  onSelect,
  onClose,
  onKeyboardClose,
  containerElement,
  menuId,
  menuLabel,
  isRoot = false,
  layerProps = {},
  keyboardModeRef
}: Readonly<MenuContentProps>): React.JSX.Element {
  const [activeChildId, setActiveChildId] = useState<string | null>(null);
  // Eagerly compute the initial focused item so SubMenuItem can move DOM
  // focus during its layout effect on first render. This avoids a second
  // render cycle before focus moves, which can be unreliable for screen
  // readers when the menu is portalled outside the trigger's DOM tree.
  const [focusedId, setFocusedId] = useState<string | null>(
    () => getInteractiveItems(items)[0]?.id ?? null
  );
  const closeRootMenuOnTab = useContext(RootMenuTabCloseContext);

  const interactiveItems = useMemo(() => getInteractiveItems(items), [items]);

  // Seed roving tabindex on first render; updated by keyboard/hover events thereafter.
  useEffect(() => {
    if (interactiveItems.length > 0 && focusedId === null) {
      setFocusedId(interactiveItems[0].id);
    }
  }, [interactiveItems, focusedId]);

  const handleDeactivate = useCallback(() => {
    setActiveChildId(null);
  }, []);

  const handleActivate = useCallback((id: string) => {
    setActiveChildId(id);
    setFocusedId(id);
  }, []);

  // Hovering a non-submenu sibling closes any open submenu (W3C menubar pattern).
  const handleHoverNonSubmenuItem = useCallback(() => {
    setActiveChildId(null);
  }, []);

  // Moves roving tabindex one step up or down, wrapping at both ends.
  const moveFocus = useCallback(
    (direction: 1 | -1) => {
      if (interactiveItems.length === 0) {
        return;
      }
      const currentIndex = interactiveItems.findIndex(
        it => it.id === focusedId
      );
      let nextIndex: number;
      if (currentIndex === -1) {
        nextIndex = direction === 1 ? 0 : interactiveItems.length - 1;
      } else {
        nextIndex =
          (currentIndex + direction + interactiveItems.length) %
          interactiveItems.length;
      }
      setFocusedId(interactiveItems[nextIndex].id);
    },
    [interactiveItems, focusedId]
  );

  // WAI-ARIA type-ahead: pressing a printable character moves focus to the next item
  // whose label starts with that character, searching forward and wrapping around.
  const handleTypeAhead = useCallback(
    (char: string) => {
      const lower = char.toLowerCase();
      const currentIndex = interactiveItems.findIndex(
        it => it.id === focusedId
      );
      const searchOrder = [
        ...interactiveItems.slice(currentIndex + 1),
        ...interactiveItems.slice(0, currentIndex + 1)
      ];
      const match = searchOrder.find(it =>
        it.label.toLowerCase().startsWith(lower)
      );
      if (match) {
        setFocusedId(match.id);
      }
    },
    [interactiveItems, focusedId]
  );

  // Escape closes the current menu level; nested ArrowLeft returns focus to its parent item.
  const closeByKeyboard = onKeyboardClose ?? onClose;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      keyboardModeRef.current = true;
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          e.stopPropagation();
          moveFocus(1);
          break;
        case 'ArrowUp':
          e.preventDefault();
          e.stopPropagation();
          moveFocus(-1);
          break;
        case 'ArrowLeft':
          if (!isRoot) {
            e.preventDefault();
            e.stopPropagation();
            closeByKeyboard();
          }
          break;
        case 'Home':
          e.preventDefault();
          e.stopPropagation();
          if (interactiveItems.length > 0) {
            setFocusedId(interactiveItems[0].id);
          }
          break;
        case 'End':
          e.preventDefault();
          e.stopPropagation();
          if (interactiveItems.length > 0) {
            setFocusedId(interactiveItems[interactiveItems.length - 1].id);
          }
          break;
        case 'Escape':
          e.preventDefault();
          e.stopPropagation();
          closeByKeyboard();
          break;
        case 'Tab':
          // Close the whole menu tree, then let the browser continue native
          // Tab/Shift+Tab from the trigger's real DOM position.
          if (closeRootMenuOnTab) {
            closeRootMenuOnTab();
          } else {
            onClose();
          }
          break;
        default:
          if (e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
            e.preventDefault();
            e.stopPropagation();
            handleTypeAhead(e.key);
          }
          break;
      }
    },
    [
      moveFocus,
      closeByKeyboard,
      interactiveItems,
      isRoot,
      handleTypeAhead,
      keyboardModeRef,
      closeRootMenuOnTab,
      onClose
    ]
  );

  return (
    <div
      {...layerProps}
      id={menuId}
      className="ehs-multilevel-menu"
      role="menu"
      aria-label={menuLabel}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
    >
      {items.map((entry, index) => {
        if (isDivider(entry)) {
          return (
            <div
              key={`divider-${index}`}
              className="ehs-multilevel-menu-divider"
              role="separator"
            />
          );
        }
        if (isHeader(entry)) {
          return (
            <div
              key={entry.id}
              className="ehs-multilevel-menu-header"
              role="none"
            >
              {entry.label}
            </div>
          );
        }
        return (
          <SubMenuItem
            key={entry.id}
            item={entry}
            isActive={activeChildId === entry.id}
            isFocused={focusedId === entry.id}
            onActivate={handleActivate}
            onDeactivate={handleDeactivate}
            onSelect={onSelect}
            onFocus={setFocusedId}
            onHoverNonSubmenuItem={handleHoverNonSubmenuItem}
            containerElement={containerElement}
            menuId={menuId}
            keyboardModeRef={keyboardModeRef}
          />
        );
      })}
    </div>
  );
}

// ── NestedSubMenu (positioned submenu for non-root levels) ──────────

type NestedSubMenuProps = {
  items: MultiLevelMenuEntry[];
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  onSelect: (item: MultiLevelMenuLeafItem) => void;
  onClose: () => void;
  onKeyboardClose: () => void;
  containerElement?: () => HTMLElement | null;
  menuId: string;
  menuLabel: string;
  keyboardModeRef: React.RefObject<boolean>;
};

function NestedSubMenu({
  items,
  anchorRef,
  onSelect,
  onClose,
  onKeyboardClose,
  containerElement,
  menuId,
  menuLabel,
  keyboardModeRef
}: Readonly<NestedSubMenuProps>): React.JSX.Element {
  // react-laag needs a trigger object to position the submenu panel, but the
  // anchor element lives in a parent component and cannot receive triggerProps.
  // useMemo keeps the object reference stable (anchorRef never changes identity)
  // so react-laag does not reposition the panel on every parent render.
  const trigger = useMemo(
    () => ({
      getBounds: () =>
        anchorRef.current?.getBoundingClientRect() ?? new DOMRect()
    }),
    [anchorRef]
  );

  const container = useMemo(() => containerElement?.() ?? undefined, [
    containerElement
  ]);

  const { layerProps, renderLayer } = useLayerDeferedVisibility({
    isOpen: true,
    container,
    trigger,
    ...SUBMENU_PLACEMENT_CONFIG
  });

  return (
    <>
      {renderLayer(
        <MenuContent
          items={items}
          onSelect={onSelect}
          onClose={onClose}
          onKeyboardClose={onKeyboardClose}
          containerElement={containerElement}
          menuId={menuId}
          menuLabel={menuLabel}
          layerProps={layerProps}
          keyboardModeRef={keyboardModeRef}
        />
      )}
    </>
  );
}

// ── MultiLevelMenuTool (exported) ───────────────────────────────────

/**
 * A Tool (button) that shows a multi-level menu when clicked.
 * Supports arbitrary nesting depth, dividers, headers, and
 * full WCAG-compliant keyboard navigation following the
 * WAI-ARIA Menu pattern.
 *
 * This component is intended for desktop use. Items with `children` display a submenu arrow and open a
 * nested submenu on hover or keyboard navigation. Submenus
 * are positioned to avoid the viewport edge using react-laag.
 *
 * @param properties {@link MultiLevelMenuToolProps} object
 * @return the created {@link React.JSX.Element}
 */
export function MultiLevelMenuTool({
  icon = 'more',
  items,
  containerElement,
  tooltipOptions = {
    delayLeave: 0
  },
  ...props
}: Readonly<MultiLevelMenuToolProps>): React.JSX.Element {
  const menuId = useId();
  const [isOpen, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const keyboardModeRef = useRef(false);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  // Close the menu and return focus to the trigger button.
  // Used when the root menu level is dismissed via keyboard (Escape).
  const handleKeyboardClose = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  const handleSelect = useCallback(
    (item: MultiLevelMenuLeafItem) => {
      handleClose();
      item.onClick();
    },
    [handleClose]
  );

  const handleOutsideClick = useCallback(() => {
    setOpen(false);
  }, []);

  // Menu layers are portalled, so native Tab would continue from the portalled
  // menu item instead of from the trigger's DOM position. Move focus back to
  // the trigger, close synchronously, and let the browser complete
  // Tab/Shift+Tab from the trigger's real DOM position.
  const handleRootTabClose = useCallback(() => {
    triggerRef.current?.focus();
    flushSync(() => setOpen(false));
  }, []);

  const handleToggle = useCallback(() => {
    setOpen(prev => {
      keyboardModeRef.current = !prev;
      return !prev;
    });
  }, []);

  const { layerProps, triggerProps, renderLayer } = useLayerDeferedVisibility({
    isOpen,
    onOutsideClick: handleOutsideClick,
    container: containerElement?.() ?? undefined,
    ...DEFAULT_PLACEMENT_CONFIG
  });

  const layer = isOpen
    ? renderLayer(
        <RootMenuTabCloseContext.Provider value={handleRootTabClose}>
          <MenuContent
            items={items}
            onSelect={handleSelect}
            onClose={handleClose}
            onKeyboardClose={handleKeyboardClose}
            containerElement={containerElement}
            menuId={menuId}
            menuLabel={props.tooltip}
            isRoot={true}
            layerProps={layerProps}
            keyboardModeRef={keyboardModeRef}
          />
        </RootMenuTabCloseContext.Provider>
      )
    : null;

  return (
    <>
      <Tool
        {...props}
        {...triggerProps}
        ref={mergeRefs<HTMLButtonElement>([triggerProps.ref, triggerRef])}
        className={
          isOpen ? 'ehs-menuTool-trigger active' : 'ehs-menuTool-trigger'
        }
        icon={icon}
        onClick={handleToggle}
        tooltip={props.tooltip}
        aria-label={props.tooltip}
        tooltipOptions={tooltipOptions}
        aria-controls={isOpen ? menuId : undefined}
        aria-expanded={isOpen}
        aria-haspopup="menu"
      />
      {layer}
    </>
  );
}
